import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectTurnOrder,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {toggleInitiativeInterception} from "../../../redux/actions";
import {
  checkIfHasRemainingConstructionActions
} from "../../gameState/construction/checkIfHasRemainingConstructionActions";
import {getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {getEnemyQuarterHexKeys, getOwnQuarterHexKeys} from "../evaluation/cities";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {isPlayerNextPolicy} from "../../factions/isPlayerNextPolicy";
import {POLICIES} from "../../../constants/policies";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {keyToCoords} from "../../coords/keyToCoords";
import {getUnitTypeConfig} from "../../../configs/units";
import {getBotExpectedMeleeDamage} from "../factions/celectialsBot";
import {isPreW32DefensePlayer} from "../abTestGates";
import {getLaterWarMoverIds, hasKillableEnemyOnOwnQuarter} from "../evaluation/cityCaptureThreat";

// Волна 27 (24.2): минимальный ожидаемый урон ближнего боя, ради которого
// стоит перехватывать инициативу для навязывания боя эльфу-кайтеру
const ELF_HUNT_MIN_EXPECTED_DAMAGE = 3;

const calcHexDistanceLocal = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

/**
 * Перехват инициативы (нюанс #7): тратит действие строительства, но дает
 * фактически две фазы войны подряд относительно противника. Перехватываем, когда:
 * - наш отряд готов захватить квартал чужого города (стоит на нем или смежно) —
 *   сыграть первым, чтобы защитник не успел отреагировать;
 * - враг угрожает нашему кварталу (стоит на нем или смежно) —
 *   сыграть первым, чтобы выбить его до захвата.
 */
export const decideInitiativeStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const turnOrder = selectTurnOrder(state);
  const nextInitiativePlayerId = selectNextInitiativePlayerId(state);

  // Гейты кнопки перехвата (UI-эквивалент)
  if (turnOrder[0] === currentPlayer.id) {
    return false; // уже владеем инициативой
  }
  if (nextInitiativePlayerId !== null) {
    return false; // кто-то уже перехватил в этом раунде
  }

  const pendingBuildings = selectPendingBuildings(state);
  const isInitiativeInterceptedWithIsolationism = selectIsInitiativeInterceptedWithIsolationism(state);
  const hasActionsRemaining = checkIfHasRemainingConstructionActions(
    currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
  );
  if (!hasActionsRemaining) {
    return false;
  }

  const players = selectPlayers(state);
  const initiativeOwner = players.find((player) => player.id === turnOrder[0]);
  if (initiativeOwner
    && (isPlayerPolicy(initiativeOwner, POLICIES.ISOLATIONISM)
      || isPlayerNextPolicy(initiativeOwner, POLICIES.ISOLATIONISM))
  ) {
    return false; // у изоляциониста инициативу не перехватить
  }

  const units = selectUnits(state);
  const citiesCache = selectCitiesCache(state);
  const gridRenderer = getBotGridRenderer(state);
  const enemyQuarters = getEnemyQuarterHexKeys(citiesCache, currentPlayer.color);
  const ownQuarters = getOwnQuarterHexKeys(citiesCache, currentPlayer.color);

  // Триггер 1: наш отряд готов к захвату (на чужом квартале или смежно с ним)
  const isCaptureImminent = units.some((unit) => {
    if (unit.ownerId !== currentPlayer.id) return false;
    if (enemyQuarters.has(unit.hexKey)) return true;
    return getNeighborHexKeys(unit.hexKey, gridRenderer).some((adjKey) => enemyQuarters.has(adjKey));
  });

  // Триггер 2: враг готов захватить наш квартал.
  // Волна 32 (оборона, гейт isPreW32DefensePlayer): захват проверяется в конце
  // раунда, а перехват (START_NEXT_PHASE поворачивает turnOrder к боту, и поворот
  // сохраняется в следующих раундах) ставит всех противников ПОСЛЕ бота. Против
  // атакующего бот так не выигрывает последний ход, только теряет: держателя
  // выбьют или обойдут без ответа, и сгорает действие стройки. Остается один
  // случай: врага на своем квартале по матожиданию добивают смежные свои отряды,
  // а его владелец ходит в войне РАНЬШЕ бота (ходящего позже бот и так бьет первым)
  let isDefenseCritical;
  if (isPreW32DefensePlayer(currentPlayer.id)) {
    isDefenseCritical = units.some((unit) => {
      if (unit.ownerId === currentPlayer.id) return false;
      if (ownQuarters.has(unit.hexKey)) return true;
      return getNeighborHexKeys(unit.hexKey, gridRenderer).some((adjKey) => ownQuarters.has(adjKey));
    });
  } else {
    // Порядок войны без перехвата (nextInitiativePlayerId здесь всегда null)
    const laterWarMoverIds = getLaterWarMoverIds({
      phase: selectPhase(state),
      turnOrder,
      nextInitiativePlayerId: null,
      phaseCompletedPlayers: selectPhaseCompletedPlayers(state),
      currentPlayerId: currentPlayer.id,
    });
    isDefenseCritical = hasKillableEnemyOnOwnQuarter({
      // Свои и нейтральные в набор не входят — урон своих отрядов считается как раньше
      units: laterWarMoverIds.size > 0 ? units.filter((unit) => !laterWarMoverIds.has(unit.ownerId)) : units,
      ownQuarters,
      currentPlayerId: currentPlayer.id,
      getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
    });
  }

  // Триггер 3 (волна 27, 24.2, запечена): эльф-кайтер досягаем нашим отрядом
  // в ЭТОМ раунде (дойти и встать смежно: дистанция - 1 <= скорость отряда), и
  // отряд нанесет хороший урон (3+ по матожиданию) — сыграть первым и навязать
  // ближний бой до того, как эльф отойдет с обстрелом (сами эльфы — вне гейта)
  let isElfHuntWindow = false;
  if (!isPlayerFaction(currentPlayer, FACTIONS.ELVES)) {
    const enemyElfCells = units
      .filter((unit) => unit.ownerId !== currentPlayer.id && unit.factionId === FACTIONS.ELVES)
      .map((unit) => unit.hexKey);
    if (enemyElfCells.length > 0) {
      isElfHuntWindow = units.some((unit) => {
        if (unit.ownerId !== currentPlayer.id) return false;
        const expectedDamage = getBotExpectedMeleeDamage(unit, currentPlayer.id);
        if (expectedDamage < ELF_HUNT_MIN_EXPECTED_DAMAGE) return false;
        const unitSpeed = getUnitTypeConfig(unit.type)?.speed || 2;
        return enemyElfCells.some(
          (elfCellKey) => calcHexDistanceLocal(unit.hexKey, elfCellKey) - 1 <= unitSpeed
        );
      });
    }
  }

  if (!isCaptureImminent && !isDefenseCritical && !isElfHuntWindow) {
    return false;
  }

  store.dispatch(toggleInitiativeInterception());
  return true;
};
